/**
 * ProductionProgressTable — Shared sortable/filterable table for production sub-sessions.
 *
 * Used by both /production-progress (standalone) and /manager/guide (produce step).
 * Includes status filter pills, sortable column headers, progress bars,
 * live elapsed time, and operator/packer status indicators.
 */
import React, { useState, useMemo, useEffect } from 'react';
import {
  ChevronUp,
  ChevronDown,
  RefreshCw,
  Clock,
  Scissors,
  Box,
} from 'lucide-react';
import { DailyWorkQueueItem } from '../services/productionService';
import { useLanguage } from '../i18n/LanguageContext';
import { getStatusBadgeClass, getStatusDotClass } from '../utils/statusColors';

// ── Helpers ───────────────────────────────────────────────

const computeElapsed = (startedAt: string | null | undefined, pausedSec: number): string => {
  if (!startedAt) return '—';
  const start = new Date(startedAt).getTime();
  const now = Date.now();
  const totalSec = Math.max(0, Math.floor((now - start) / 1000) - pausedSec);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

// ── Types ─────────────────────────────────────────────────

type SortColumn =
  | 'station_name'
  | 'queue_position'
  | 'product_name'
  | 'operator_name'
  | 'packer_name'
  | 'status'
  | 'boxes'
  | 'weight'
  | 'elapsed';

// Sort rank for sub-session statuses (lower = more blocking, sorts first).
// Values MUST stay in sync with the canonical map in
// `services/shared/session-status/index.js` (STATUS_RANK). Kept as a local
// constant because admin-frontend's Vite/Rollup build can't currently
// statically resolve named imports from the CommonJS shared package;
// structural unification is tracked as DUP-01 follow-up (needs a dual ESM/CJS
// build for @tnb/session-status before the workspace dep can be reintroduced).
const STATUS_RANK: Record<string, number> = {
  active: 1,
  paused: 2,
  ready: 3,
  received: 4,
  submitted: 5,
  pending: 6,
  cancelled: 7,
  completed: 8,
};

const STATUS_OPTIONS = ['active', 'paused', 'ready', 'received', 'submitted', 'pending', 'completed', 'cancelled'] as const;

// ── Sortable Header ───────────────────────────────────────

function SortableHeader({
  field,
  label,
  sortField,
  sortDir,
  onSort,
  className = '',
}: {
  field: SortColumn;
  label: string;
  sortField: SortColumn;
  sortDir: 'asc' | 'desc';
  onSort: (f: SortColumn) => void;
  className?: string;
}) {
  const active = sortField === field;
  return (
    <th
      className={`px-4 py-3 cursor-pointer select-none group whitespace-nowrap ${className}`}
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {active && sortDir === 'asc' ? (
          <ChevronUp className="w-3.5 h-3.5 text-indigo-500" />
        ) : active && sortDir === 'desc' ? (
          <ChevronDown className="w-3.5 h-3.5 text-indigo-500" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5 opacity-0 group-hover:opacity-30" />
        )}
      </div>
    </th>
  );
}

// ── Table Row ─────────────────────────────────────────────

function ProgressRow({ item }: { item: DailyWorkQueueItem }) {
  const { t } = useLanguage();
  const boxPct = item.expected_boxes > 0 ? Math.min(100, (item.boxes_produced / item.expected_boxes) * 100) : 0;
  const elapsed = computeElapsed(item.started_at, item.paused_duration_seconds ?? 0);

  const barColor = boxPct >= 100
    ? 'bg-green-500'
    : boxPct >= 50
      ? 'bg-blue-500'
      : 'bg-indigo-500';

  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
      <td className="px-4 py-3 font-medium whitespace-nowrap">
        {item.station_name || '—'}
      </td>
      <td className="px-4 py-3 text-center whitespace-nowrap font-mono text-xs">
        {item.queue_position != null ? `#${item.queue_position}` : '—'}
      </td>
      <td className="px-4 py-3">
        <div className="font-medium">{item.product_name}</div>
        {item.product_chinese_name && (
          <div className="text-xs text-gray-500 dark:text-gray-400">{item.product_chinese_name}</div>
        )}
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <span className={`inline-flex items-center gap-1 text-xs ${
          item.operator_status === 'on_break'
            ? 'bg-amber-100 dark:bg-amber-900/30 rounded-full px-2 py-0.5 animate-pulse'
            : item.operator_status === 'scanned_out' && item.operator_name
              ? 'bg-red-100 dark:bg-red-900/30 rounded-full px-2 py-0.5'
              : ''
        }`}>
          <Scissors className="w-3 h-3 text-green-500" />
          {item.operator_name || '—'}
        </span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <span className={`inline-flex items-center gap-1 text-xs ${
          item.packer_status === 'on_break'
            ? 'bg-amber-100 dark:bg-amber-900/30 rounded-full px-2 py-0.5 animate-pulse'
            : item.packer_status === 'scanned_out' && item.packer_name
              ? 'bg-red-100 dark:bg-red-900/30 rounded-full px-2 py-0.5'
              : ''
        }`}>
          <Box className="w-3 h-3 text-blue-500" />
          {item.packer_name || '—'}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(item.status)}`}>
          {t(`productionProgress.status.${item.status}`)}
        </span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap font-mono text-xs">
        {item.boxes_produced} / {item.expected_boxes}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-2 min-w-[80px]">
            <div
              className={`${barColor} h-2 rounded-full transition-all duration-300`}
              style={{ width: `${boxPct}%` }}
            />
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400 w-16 text-right">
            {boxPct.toFixed(2)}%
          </span>
        </div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap font-mono text-xs">
        {(item.actual_output_weight ?? 0).toFixed(2)} / {(item.target_output_weight ?? 0).toFixed(2)}
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <div className={`flex items-center gap-1 text-sm ${
          item.status === 'paused'
            ? 'text-yellow-600 dark:text-yellow-400'
            : item.status === 'active'
              ? 'text-green-600 dark:text-green-400'
              : 'text-gray-500 dark:text-gray-400'
        }`}>
          <Clock className="w-3 h-3" />
          {elapsed}
        </div>
      </td>
    </tr>
  );
}

// ── Main Component ────────────────────────────────────────

interface ProductionProgressTableProps {
  items: DailyWorkQueueItem[];
  isLoading: boolean;
  /** When true, strips the outer card wrapper so it can be embedded inside a parent card */
  embedded?: boolean;
}

const ProductionProgressTable: React.FC<ProductionProgressTableProps> = ({ items, isLoading, embedded }) => {
  const { t } = useLanguage();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [sortField, setSortField] = useState<SortColumn>('status');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [, setTick] = useState(0);

  // Tick every second for live elapsed time
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Filter
  const filtered = useMemo(() => {
    if (!statusFilter) return items;
    return items.filter((i) => i.status === statusFilter);
  }, [items, statusFilter]);

  // Sort
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let av: unknown, bv: unknown;
      switch (sortField) {
        case 'status':
          av = STATUS_RANK[a.status] ?? 99;
          bv = STATUS_RANK[b.status] ?? 99;
          break;
        case 'queue_position':
          av = a.queue_position ?? Infinity;
          bv = b.queue_position ?? Infinity;
          break;
        case 'boxes':
          av = a.boxes_produced;
          bv = b.boxes_produced;
          break;
        case 'weight':
          av = a.actual_output_weight ?? 0;
          bv = b.actual_output_weight ?? 0;
          break;
        case 'elapsed':
          av = a.started_at ? new Date(a.started_at).getTime() : Infinity;
          bv = b.started_at ? new Date(b.started_at).getTime() : Infinity;
          break;
        default:
          av = (a as unknown as Record<string, unknown>)[sortField] ?? '';
          bv = (b as unknown as Record<string, unknown>)[sortField] ?? '';
      }
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      const sa = String(av).toLowerCase(), sb = String(bv).toLowerCase();
      return sortDir === 'asc' ? sa.localeCompare(sb) : sb.localeCompare(sa);
    });
  }, [filtered, sortField, sortDir]);

  const handleSort = (field: SortColumn) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // Status counts for filter badges
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const item of items) {
      counts[item.status] = (counts[item.status] || 0) + 1;
    }
    return counts;
  }, [items]);

  return (
    <>
      {/* Status Filter Pills */}
      <div className="flex flex-wrap gap-1.5">
        <button
          onClick={() => setStatusFilter('')}
          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
            !statusFilter
              ? 'bg-gray-800 text-white dark:bg-white dark:text-gray-800'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          {t('productionProgress.all')} ({items.length})
        </button>
        {STATUS_OPTIONS.map((s) => {
          const count = statusCounts[s] || 0;
          const isActive = statusFilter === s;
          return (
            <button
              key={s}
              onClick={() => setStatusFilter(isActive ? '' : s)}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                isActive
                  ? getStatusBadgeClass(s)
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${getStatusDotClass(s)} ${count === 0 ? 'opacity-30' : ''}`} />
              {t(`productionProgress.status.${s}`)} ({count})
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div className={embedded ? 'overflow-x-auto' : 'bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-x-auto'}>
        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : sorted.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            {t('productionProgress.noSessions')}
          </div>
        ) : (
          <table className="w-full text-sm text-gray-900 dark:text-gray-100">
            <thead className="bg-gray-50 dark:bg-gray-700/50 text-left text-gray-600 dark:text-gray-300">
              <tr>
                <SortableHeader field="station_name" label={t('productionProgress.station')} sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader field="queue_position" label={t('productionProgress.queue')} sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader field="product_name" label={t('productionProgress.product')} sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader field="operator_name" label={t('productionProgress.operator')} sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader field="packer_name" label={t('productionProgress.packer')} sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader field="status" label={t('productionProgress.status_header')} sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader field="boxes" label={t('productionProgress.boxes')} sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <th className="px-4 py-3 min-w-[120px]">{t('productionProgress.progress')}</th>
                <SortableHeader field="weight" label={t('productionProgress.weight')} sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader field="elapsed" label={t('productionProgress.elapsed')} sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {sorted.map((item) => (
                <ProgressRow key={item.sub_session_number} item={item} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

export default ProductionProgressTable;
